import { Mesh, PointLight, Vector3, type BufferGeometry, type Object3D } from 'three';
import { MeshBVH, type MeshBVHOptions } from 'three-mesh-bvh';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  BAKED_LIGHT_ATTR,
  bakeJob,
  fromKernelAttributes,
  tessellateJob,
  toKernelAttributes,
  transferables,
  type KernelAttributes,
  type KernelLight,
  type KernelResult,
} from './bake-kernel';
import {
  BAKE_CACHE_VERSION,
  decodeBakeCache,
  encodeBakeCache,
  hashFloats,
  hashNumbers,
} from './bake-cache';

/**
 * THE LIGHTMAPPER.
 *
 * Every fixture in the mall — the vitrine strips, the supermarket coolers, the
 * fluorescent battens over the service corridor, the lamp posts on the balcony —
 * is authored as a real `PointLight` right where it belongs, because that is the
 * only way the world files stay readable. Rendering them is another matter.
 * Three.js is a forward renderer: it evaluates every light in the scene for
 * every lit fragment on screen, whether or not the light is anywhere near it.
 * Measured on an M4 at 1.5x device pixel ratio on a 1960x1063 window:
 *
 *      point lights    0     4     8    12    24    47
 *      frames/sec     60    60    60    30    15     3
 *
 * Three frames a second for a building whose lights are bolted to its ceilings
 * and never move. So they do not get rendered at all. This runs once at load,
 * computes what each fixture does to every vertex it can reach, and writes the
 * answer into the geometry. The lights are then deleted. The frame cost of shop
 * lighting for the rest of the session is zero.
 *
 * WHAT THIS BUYS BEYOND SPEED. A baked light can be occluded, and a realtime
 * point light in three.js cannot — only the single directional moon casts
 * shadows, so as realtime lights the shop fittings shone straight through their
 * own walls and lit the unit next door. Here every vertex traces a ray to every
 * fixture that can reach it, so light stops at walls, shelving racks throw
 * shadows onto the floor behind them, and a lit unit next to a dark one actually
 * reads as two rooms rather than one gradient. That is the picture from the
 * reference sheet: pools of light with hard edges of dark between them.
 *
 * WHAT IT COSTS. Vertex lighting is only as detailed as the mesh under it, and a
 * floor slab is two triangles, so everything a light can reach is subdivided
 * first — see `subdivide` in `bake-kernel.ts`. It is diffuse-only, with no
 * specular highlight from the fittings, which for a building of matte plaster,
 * painted steel and worn tile is the right trade and arguably the better look.
 *
 * WHAT IT RULES OUT. Nothing here can move. A light that needs to move — a
 * torch, an alarm strobe, a player — has to be a real light, and there is a
 * small pool for exactly that in `LightDirector`. The budget for those is what
 * the table above says it is, so they need to be spent carefully.
 *
 * WHY IT IS PARALLEL. Eight million shadow rays took twenty-four seconds on one
 * thread, which is twenty-four seconds of a frozen tab that cannot even animate
 * a loading bar. Every mesh is independent and the occluder tree is read-only,
 * so the work goes out to one worker per core. The tree is built once here and
 * shipped over already constructed; rebuilding it per worker would cost more
 * than the parallelism returns.
 */

export interface StaticLight {
  position: Vector3;
  /** Colour premultiplied by intensity — what the shader would have used. */
  radiance: Vector3;
  /** Cutoff distance. Zero would mean infinite reach, which nothing here wants. */
  distance: number;
  decay: number;
}

export interface BakeReport {
  lights: number;
  meshes: number;
  vertices: number;
  /** Vertices that received any light at all. The rest are moonlight only. */
  lit: number;
  rays: number;
  /** Cores actually used. One means the worker path was unavailable. */
  workers: number;
  /** Time spent building the occluder tree, which is serial and unavoidable. */
  bvhMs: number;
  /** Time spent shipping that tree into every worker, also serial. */
  initMs: number;
  /** The single longest mesh. If this approaches `ms`, one batch is the bake. */
  slowest: number;
  /** Percent of available core-time actually spent working. 100 is perfect. */
  balance: number;
  /** De-indexing the whole map before any of it can be handed out. Serial. */
  flatMs: number;
  /** Slicing chunks out and rebuilding geometry from results. Also serial, and
   *  it runs BETWEEN worker results, so if it is large the pool is starving. */
  mainMs: number;
  ms: number;
  /** How the light got here: computed now, or read off disk. */
  source: 'live' | 'cache';
  /**
   * Set when a cache was offered and refused. The loud half of the signature
   * check — the file is there, it is just describing a different building, and
   * somebody needs to rerun `npm run bake`.
   */
  stale: boolean;
  /** Identifies this world and this kernel. See `bake-cache.ts`. */
  signature: number;
}

export interface BakeOptions {
  onProgress?: (done: number, total: number) => void;
  /**
   * A precomputed lightmap, as fetched. Applied only if its signature matches
   * the world being baked; otherwise it is ignored and the bake runs live.
   */
  cache?: ArrayBuffer | null;
  /** Handed the encodable result of a LIVE bake, for `tools/bake.ts` to save. */
  onEncoded?: (buffer: ArrayBuffer) => void;
}

/**
 * Take every `PointLight` out of a subtree and return it as data.
 *
 * Destructive, and it has to be: leaving them in place at zero intensity would
 * keep the full per-fragment cost, because a forward renderer charges for a
 * light by its presence in the scene rather than by its brightness.
 */
export function harvestPointLights(root: Object3D): StaticLight[] {
  root.updateMatrixWorld(true);
  const found: PointLight[] = [];
  root.traverse((o) => {
    if ((o as PointLight).isPointLight) found.push(o as PointLight);
  });
  return found.map((l) => {
    const light: StaticLight = {
      position: l.getWorldPosition(new Vector3()),
      radiance: new Vector3(l.color.r, l.color.g, l.color.b).multiplyScalar(l.intensity),
      // A fixture authored without a cutoff would reach the whole building and
      // make the bake quadratic in the size of the map.
      distance: l.distance || 18,
      decay: l.decay,
    };
    l.removeFromParent();
    l.dispose();
    return light;
  });
}

/**
 * Which meshes take baked light.
 *
 * Transparent surfaces are skipped: glazing, additive light pools, the fake glow
 * quads. They are unlit by intent, and baking into them would put a bright smear
 * on every pane of shopfront glass in the building.
 */
function collect(root: Object3D): Mesh[] {
  const meshes: Mesh[] = [];
  const seen = new Set<string>();
  root.traverse((o) => {
    const m = o as Mesh;
    if (!m.isMesh || !m.geometry?.attributes?.position) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    if (mats.some((x) => x?.transparent)) return;
    // Two meshes sharing one geometry would each write a different answer into
    // the same buffer and the last one would win — visible as a prop lit for
    // somewhere it is not standing. Give the second one its own copy.
    if (seen.has(m.geometry.uuid)) m.geometry = m.geometry.clone();
    seen.add(m.geometry.uuid);
    meshes.push(m);
  });
  return meshes;
}

/**
 * One acceleration structure over the whole static world, for shadow rays.
 *
 * Built from a merged copy holding positions only. It is a second full copy of
 * the map's geometry for the duration of the bake, which is a few tens of
 * megabytes and is released immediately afterwards — far cheaper than the
 * alternative of one BVH per mesh and a per-ray loop over thousands of them.
 */
function buildOccluders(meshes: Mesh[]): BufferGeometry | null {
  const parts: BufferGeometry[] = [];
  for (const m of meshes) {
    const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
    g.applyMatrix4(m.matrixWorld);
    const p = g.getAttribute('position');
    for (const name of Object.keys(g.attributes)) if (name !== 'position') g.deleteAttribute(name);
    g.setAttribute('position', p);
    parts.push(g);
  }
  const merged = mergeGeometries(parts);
  for (const g of parts) g.dispose();
  return merged;
}

function toKernelLights(lights: StaticLight[]): KernelLight[] {
  return lights.map((l) => ({
    p: [l.position.x, l.position.y, l.position.z] as const,
    rad: [l.radiance.x, l.radiance.y, l.radiance.z] as const,
    distance: l.distance,
    decay: l.decay,
  }));
}

/** Swap a kernel result into the mesh it came from. */
function apply(mesh: Mesh, attributes: KernelAttributes): number {
  const geo = fromKernelAttributes(attributes);
  mesh.geometry.dispose();
  mesh.geometry = geo;
  return geo.getAttribute('position').count;
}

/**
 * Roughly how many BAKED vertices one unit of work should produce.
 *
 * The size distribution across this map is extreme: static batching merges a
 * whole wing into one mesh, so a handful of batches carry most of the building
 * and a thousand props carry the rest. Whole meshes as jobs measured at seven
 * and a half seconds for the worst one out of a thirteen-second bake — seven
 * cores idling while one finished the atrium. It is worse than it sounds on
 * Apple silicon, where over half the cores are efficiency cores at roughly a
 * third the speed, so any long job is a coin flip on landing somewhere slow.
 *
 * Splitting by INPUT vertex count does not fix it, and measuring that was the
 * useful part: a three-thousand-vertex chunk still took four and a half seconds,
 * because the cost of a triangle is what it costs after subdivision and a floor
 * slab is two enormous triangles that become thousands. The unit of work has to
 * be estimated in output, which means area.
 */
const CHUNK_WORK = 20_000;

/**
 * Area of one triangle at the target resolution, used to estimate that output.
 *
 * `subdivide` splits until no edge exceeds `MAX_EDGE`, so a triangle of area A
 * ends up as roughly A over this many pieces — capped, because subdivision gives
 * up after a fixed depth.
 */
const TARGET_TRI_AREA = 0.5;
const MAX_SPLIT = 32;

/** One chunk of one mesh. `chunk` is its position in that mesh's output. */
interface Job {
  mesh: number;
  chunk: number;
  start: number;
  count: number;
  /** Estimated baked vertices, for ordering. See `CHUNK_WORK`. */
  work: number;
}

/**
 * Chunks are ranges into the DE-INDEXED buffer, so the plan has to be made from
 * the de-indexed buffer.
 *
 * Planning from `geometry.attributes.position.count` instead looks right and is
 * not: an indexed geometry has fewer positions than it has corners, so a third
 * of the building silently never got baked, and any mesh whose vertex count was
 * not a multiple of three handed a worker two thirds of a triangle, which comes
 * back as NaN.
 */
function planJobs(flat: KernelAttributes[]): Job[] {
  const jobs: Job[] = [];
  for (let m = 0; m < flat.length; m++) {
    const p = flat[m].position.array;
    const verts = p.length / 3;
    let chunk = 0;
    let start = 0;
    let work = 0;
    for (let i = 0; i < verts; i += 3) {
      const o = i * 3;
      const e1x = p[o + 3] - p[o], e1y = p[o + 4] - p[o + 1], e1z = p[o + 5] - p[o + 2];
      const e2x = p[o + 6] - p[o], e2y = p[o + 7] - p[o + 1], e2z = p[o + 8] - p[o + 2];
      const cx = e1y * e2z - e1z * e2y;
      const cy = e1z * e2x - e1x * e2z;
      const cz = e1x * e2y - e1y * e2x;
      const area = 0.5 * Math.hypot(cx, cy, cz);
      work += 3 * Math.min(MAX_SPLIT, Math.max(1, area / TARGET_TRI_AREA));
      if (work < CHUNK_WORK) continue;
      jobs.push({ mesh: m, chunk: chunk++, start, count: i + 3 - start, work });
      start = i + 3;
      work = 0;
    }
    if (start < verts) jobs.push({ mesh: m, chunk: chunk++, start, count: verts - start, work });
  }
  /**
   * Heaviest first. The estimate is crude, so this is not load balancing — the
   * pool does that by pulling. It only makes sure the one job that turns out to
   * be the longest is not the one handed out last, which is the whole tail.
   */
  jobs.sort((a, b) => b.work - a.work);
  return jobs;
}

/** Pull one chunk's vertices out of a flattened mesh as a standalone set. */
function slice(src: KernelAttributes, job: Job): KernelAttributes {
  const out: KernelAttributes = {};
  for (const name of Object.keys(src)) {
    const { array, itemSize } = src[name];
    out[name] = {
      array: array.slice(job.start * itemSize, (job.start + job.count) * itemSize),
      itemSize,
    };
  }
  return out;
}

/** Reassemble a mesh's chunks, in order, into one attribute set. */
function concat(parts: KernelAttributes[]): KernelAttributes {
  if (parts.length === 1) return parts[0];
  const out: KernelAttributes = {};
  for (const name of Object.keys(parts[0])) {
    const itemSize = parts[0][name].itemSize;
    let length = 0;
    for (const p of parts) length += p[name].array.length;
    const array = new Float32Array(length);
    let at = 0;
    for (const p of parts) {
      array.set(p[name].array, at);
      at += p[name].array.length;
    }
    out[name] = { array, itemSize };
  }
  return out;
}

/**
 * How many cores to spend.
 *
 * One left for the main thread, which still has to keep the page responsive and
 * hand out jobs. Capped at eight because past that the per-worker copy of the
 * occluder tree costs more memory than the extra core saves in time.
 */
function workerCount(): number {
  const cores = navigator.hardwareConcurrency || 4;
  return Math.max(1, Math.min(8, cores - 1));
}

/**
 * Bake `lights` into every static mesh under `root`, across every core available.
 *
 * Must run after the world is finished and after static batching, because it
 * reads world-space positions and rewrites geometry in place — anything that
 * moves afterwards carries lighting from where it used to be.
 *
 * Falls back to the single-threaded path if workers cannot be constructed, so a
 * restricted embedding still gets the same picture, just slowly.
 */
export async function bakeStaticLight(
  root: Object3D,
  lights: StaticLight[],
  opts: BakeOptions = {},
): Promise<BakeReport> {
  const { onProgress } = opts;
  const t0 = performance.now();
  root.updateMatrixWorld(true);

  const meshes = collect(root);
  const kernelLights = toKernelLights(lights);

  // One de-indexed copy of every lit surface in the building, held only for as
  // long as it takes to hand its chunks out. Dropped per mesh as it completes.
  //
  // Hoisted above the occluder tree so the signature can be computed before
  // deciding whether the tree is needed at all: a cached load skips it, and it
  // is several seconds of the load it is skipping.
  const tFlat = performance.now();
  const flat: Array<KernelAttributes | null> = meshes.map((m) => toKernelAttributes(m.geometry));
  const jobs = planJobs(flat as KernelAttributes[]);
  const flatMs = Math.round(performance.now() - tFlat);

  const signature = worldSignature(meshes, flat as KernelAttributes[], kernelLights);
  const cache = opts.cache ? decodeBakeCache(opts.cache, signature) : null;
  const stale = Boolean(opts.cache) && !cache;
  if (stale) {
    console.warn(
      '[mall] the lightmap on disk does not match this build — baking live. Run `npm run bake`.',
    );
  }
  if (cache && cache.counts.length !== meshes.length) {
    // Belt to the signature's braces. Same-signature-different-mesh-count is
    // not reachable by any change I can construct, and if it ever is, an unlit
    // building with a line in the console beats a silently misaligned one.
    console.warn('[mall] lightmap mesh count disagrees; baking live.');
  }
  const cached = cache && cache.counts.length === meshes.length ? cache : null;

  const tBvh = performance.now();
  const shadowGeo = cached ? null : buildOccluders(meshes);
  const bvh = shadowGeo ? new MeshBVH(shadowGeo, { maxLeafTris: 12 } as MeshBVHOptions) : null;
  const bvhMs = Math.round(performance.now() - tBvh);

  /** Per-mesh irradiance, kept only when somebody asked to save it. */
  const encode: Float32Array[] | null = opts.onEncoded && !cached ? meshes.map(() => EMPTY) : null;
  let mainMs = 0;

  let vertices = 0;
  let lit = 0;
  let rays = 0;
  let workers = 1;
  let initMs = 0;
  let slowest = 0;
  let balance = 100;

  /** Chunks still outstanding per mesh, and the results already back. */
  const outstanding = meshes.map(() => 0);
  for (const j of jobs) outstanding[j.mesh]++;
  const gathered: KernelAttributes[][] = meshes.map(() => []);

  const land = (job: Job, out: KernelResult) => {
    const tm = performance.now();
    lit += out.lit;
    rays += out.rays;
    gathered[job.mesh][job.chunk] = out.attributes;
    if (--outstanding[job.mesh] === 0) {
      const whole = concat(gathered[job.mesh]);
      // The cached path tessellated and nothing else; the light goes on here,
      // after the chunks are back in order, because that order is what the
      // file was written in.
      if (cached) lit += graft(whole, cached.read(job.mesh), job.mesh);
      else if (encode) encode[job.mesh] = whole[BAKED_LIGHT_ATTR].array.slice();
      vertices += apply(meshes[job.mesh], whole);
      gathered[job.mesh] = [];
      // The flattened copy was only ever scratch for slicing chunks out of.
      flat[job.mesh] = null;
    }
    mainMs += performance.now() - tm;
  };

  const pool = bvh || cached ? spawn(workerCount()) : [];
  if (pool.length) {
    workers = pool.length;
    const tInit = performance.now();
    const serialized = bvh ? MeshBVH.serialize(bvh, { cloneBuffers: false }) : null;
    const positions = shadowGeo
      ? (shadowGeo.getAttribute('position').array as Float32Array)
      : null;

    // Structured clone, not transfer: every worker needs its own copy of the
    // same tree, and a transferred buffer can only go to one of them.
    await Promise.all(
      pool.map(
        (w) =>
          new Promise<void>((done) => {
            w.addEventListener('message', function ready(e: MessageEvent) {
              if ((e.data as { kind: string }).kind !== 'ready') return;
              w.removeEventListener('message', ready);
              done();
            });
            w.postMessage({ kind: 'init', positions, bvh: serialized, lights: kernelLights });
          }),
      ),
    );

    initMs = Math.round(performance.now() - tInit);

    const tJobs = performance.now();
    let next = 0;
    let finished = 0;
    /** Which chunk each worker is currently chewing on. */
    const inFlight = new Map<Worker, Job>();
    /** Wall time each worker spent with a job outstanding, for `balance`. */
    const busy = new Map<Worker, number>();
    const started = new Map<Worker, number>();
    await Promise.all(
      pool.map(
        (w) =>
          new Promise<void>((done) => {
            const send = () => {
              if (next >= jobs.length) {
                done();
                return;
              }
              const job = jobs[next++];
              const ts = performance.now();
              const attributes = slice(flat[job.mesh]!, job);
              mainMs += performance.now() - ts;
              inFlight.set(w, job);
              started.set(w, performance.now());
              w.postMessage(
                { kind: 'job', attributes, matrix: meshes[job.mesh].matrixWorld.elements },
                transferables(attributes),
              );
            };
            w.onmessage = (e: MessageEvent<KernelResult & { kind: string }>) => {
              const job = inFlight.get(w)!;
              const took = performance.now() - started.get(w)!;
              busy.set(w, (busy.get(w) ?? 0) + took);
              slowest = Math.max(slowest, Math.round(took));
              land(job, e.data);
              onProgress?.(++finished, jobs.length);
              send();
            };
            send();
          }),
      ),
    );
    const jobMs = performance.now() - tJobs;
    let sum = 0;
    for (const v of busy.values()) sum += v;
    balance = Math.round((sum / (pool.length * jobMs)) * 100);
    for (const w of pool) w.terminate();
  } else {
    // Single-threaded fallback. Same kernel, same answer, twenty-odd seconds.
    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const attributes = slice(flat[job.mesh]!, job);
      const one = { attributes, matrix: meshes[job.mesh].matrixWorld.elements };
      land(job, cached ? tessellateJob(one, kernelLights) : bakeJob(one, kernelLights, bvh));
      onProgress?.(i + 1, jobs.length);
    }
  }

  shadowGeo?.dispose();
  if (encode) opts.onEncoded!(encodeBakeCache(signature, encode));

  return {
    lights: lights.length,
    meshes: meshes.length,
    vertices,
    lit,
    rays,
    workers,
    bvhMs,
    initMs,
    flatMs,
    mainMs: Math.round(mainMs),
    slowest,
    balance,
    ms: Math.round(performance.now() - t0),
    source: cached ? 'cache' : 'live',
    stale,
    signature,
  };
}

const EMPTY = /* @__PURE__ */ new Float32Array(0);

/**
 * Copy one mesh's irradiance out of the cache and into its freshly tessellated
 * geometry. Returns how many vertices got any light, so the report reads the
 * same either way.
 */
function graft(whole: KernelAttributes, values: Float32Array, mesh: number): number {
  const dst = whole[BAKED_LIGHT_ATTR].array;
  if (dst.length !== values.length) {
    // The signature said this world is the world the file was baked from, so
    // reaching here means the subdivision is no longer deterministic — a real
    // bug, and one that would otherwise show up as a mall lit slightly wrong.
    console.error(
      `[mall] lightmap mesh ${mesh} expected ${dst.length / 3} vertices, tessellation produced ${
        values.length / 3
      }; leaving it dark.`,
    );
    return 0;
  }
  dst.set(values);
  let lit = 0;
  for (let i = 0; i < dst.length; i += 3) if (dst[i] + dst[i + 1] + dst[i + 2] > 0) lit++;
  return lit;
}

/**
 * One number that changes whenever the baked answer would change.
 *
 * Everything the kernel reads goes in: the de-indexed source positions of every
 * lit mesh (which is also, transitively, the tessellation), the world transform
 * each one is baked at, and every light's position, radiance, reach and falloff.
 * The kernel's own constants cannot be hashed — they are code — so they sit
 * behind `BAKE_CACHE_VERSION` instead.
 *
 * Reading the raw float bytes rather than anything rounded is deliberate; see
 * `hashFloats`.
 */
function worldSignature(
  meshes: Mesh[],
  flat: KernelAttributes[],
  lights: KernelLight[],
): number {
  let h = hashNumbers(0x811c9dc5, [BAKE_CACHE_VERSION, meshes.length, lights.length]);
  for (let i = 0; i < flat.length; i++) {
    h = hashFloats(h, flat[i].position.array);
    h = hashNumbers(h, meshes[i].matrixWorld.elements);
  }
  for (const l of lights) {
    h = hashNumbers(h, [...l.p, ...l.rad, l.distance, l.decay]);
  }
  return h >>> 0;
}

function spawn(count: number): Worker[] {
  const pool: Worker[] = [];
  try {
    for (let i = 0; i < count; i++) {
      pool.push(new Worker(new URL('./bake-worker.ts', import.meta.url), { type: 'module' }));
    }
  } catch {
    for (const w of pool) w.terminate();
    return [];
  }
  return pool;
}
